import { animate } from 'framer-motion';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import {
  TYPING_RULE_TEXT,
  TIMING_MS,
  type DemoPhase,
  type DemoTab
} from './landingRoleRulesDemoData';
import { mockPointerClick, sleep } from './landingMockPointerUtils';

export interface OverlayBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FlyVisual extends OverlayBox {
  opacity: number;
  blurPx: number;
  scale: number;
}

const FLY_PILL_W = 52;
const FLY_PILL_H = 22;
const DROP_OFFSET_Y = 36;

/** Soft landing onto Save — slight overshoot */
const EASE_DROP_ON_SAVE: [number, number, number, number] = [0.34, 1.28, 0.52, 1];
/** Gravity-style fall into History */
const EASE_DROP_TO_HISTORY: [number, number, number, number] = [0.22, 1, 0.36, 1];

function centerOf(el: HTMLElement, stage: DOMRect): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return {
    x: r.left + r.width / 2 - stage.left,
    y: r.top + r.height / 2 - stage.top
  };
}

function pillVisualAt(center: { x: number; y: number }, partial?: Partial<FlyVisual>): FlyVisual {
  return {
    x: center.x - FLY_PILL_W / 2,
    y: center.y - FLY_PILL_H / 2,
    width: FLY_PILL_W,
    height: FLY_PILL_H,
    opacity: 1,
    blurPx: 0,
    scale: 1,
    ...partial
  };
}

function visualCenter(v: FlyVisual): { x: number; y: number } {
  return { x: v.x + v.width / 2, y: v.y + v.height / 2 };
}

/** Quadratic-bezier control point — bulges the path upward/outward between endpoints */
function arcControlPoint(
  from: FlyVisual,
  to: FlyVisual,
  lift: number
): { x: number; y: number } {
  const c0 = visualCenter(from);
  const c2 = visualCenter(to);
  const dx = c2.x - c0.x;
  const dy = c2.y - c0.y;
  const dist = Math.hypot(dx, dy) || 1;
  const mx = (c0.x + c2.x) / 2;
  const my = (c0.y + c2.y) / 2;
  const perpX = (-dy / dist) * dist * lift * 0.35;
  const perpY = (dx / dist) * dist * lift * 0.2;
  const liftY = -dist * lift;
  return { x: mx + perpX, y: my + perpY + liftY };
}

function lerpFlyScalars(from: FlyVisual, to: FlyVisual, t: number): Pick<FlyVisual, 'opacity' | 'blurPx' | 'scale'> {
  return {
    opacity: from.opacity + (to.opacity - from.opacity) * t,
    blurPx: from.blurPx + (to.blurPx - from.blurPx) * t,
    scale: from.scale + (to.scale - from.scale) * t
  };
}

function lerpFly(from: FlyVisual, to: FlyVisual, t: number): FlyVisual {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
    width: from.width,
    height: from.height,
    ...lerpFlyScalars(from, to, t)
  };
}

function lerpFlyArc(
  from: FlyVisual,
  to: FlyVisual,
  t: number,
  control: { x: number; y: number }
): FlyVisual {
  const c0 = visualCenter(from);
  const c2 = visualCenter(to);
  const u = 1 - t;
  const cx = u * u * c0.x + 2 * u * t * control.x + t * t * c2.x;
  const cy = u * u * c0.y + 2 * u * t * control.y + t * t * c2.y;
  return {
    x: cx - from.width / 2,
    y: cy - from.height / 2,
    width: from.width,
    height: from.height,
    ...lerpFlyScalars(from, to, t)
  };
}

type TweenFlyOptions = {
  arcLift?: number;
  onProgress?: (t: number) => void;
};

async function tweenFly(
  from: FlyVisual,
  to: FlyVisual,
  durationMs: number,
  ease: [number, number, number, number],
  onUpdate: (v: FlyVisual) => void,
  cancelled: () => boolean,
  options?: TweenFlyOptions
) {
  if (cancelled()) return;
  const arcLift = options?.arcLift ?? 0;
  const control = arcLift > 0 ? arcControlPoint(from, to, arcLift) : null;

  await new Promise<void>((resolve) => {
    const controls = animate(0, 1, {
      duration: durationMs / 1000,
      ease,
      onUpdate: (t) => {
        if (cancelled()) {
          controls.stop();
          return;
        }
        const visual =
          control != null ? lerpFlyArc(from, to, t, control) : lerpFly(from, to, t);
        onUpdate(visual);
        options?.onProgress?.(t);
      },
      onComplete: () => {
        if (!cancelled()) resolve();
      }
    });
  });
}

const INITIAL_FLY: FlyVisual = {
  x: 0,
  y: 0,
  width: FLY_PILL_W,
  height: FLY_PILL_H,
  opacity: 0,
  blurPx: 0,
  scale: 1
};

export function useRoleRulesLoop(enabled: boolean) {
  const stageRef = useRef<HTMLDivElement>(null);
  const saveRef = useRef<HTMLSpanElement>(null);
  const historyTabRef = useRef<HTMLButtonElement>(null);

  const [phase, setPhase] = useState<DemoPhase>('idle');
  const [ruleText, setRuleText] = useState('');
  const [activeTab] = useState<DemoTab>('edit');
  const [isSaving, setIsSaving] = useState(false);
  const [showPropagate, setShowPropagate] = useState(false);
  const [showFly, setShowFly] = useState(false);
  const [flyVisual, setFlyVisual] = useState<FlyVisual>(INITIAL_FLY);
  const [saveHighlight, setSaveHighlight] = useState(false);
  const [historyRipple, setHistoryRipple] = useState(false);
  const [pointerClicking, setPointerClicking] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [measureReady, setMeasureReady] = useState(false);

  const cancelledRef = useRef(false);

  const measureFlyTargets = useCallback((): {
    dropStart: FlyVisual;
    save: FlyVisual;
    history: FlyVisual;
  } | null => {
    const stage = stageRef.current;
    const save = saveRef.current;
    const history = historyTabRef.current;
    if (!stage || !save || !history) return null;
    const stageRect = stage.getBoundingClientRect();
    const saveCenter = centerOf(save, stageRect);
    const historyCenter = centerOf(history, stageRect);
    const saveVisual = pillVisualAt(saveCenter);
    return {
      dropStart: pillVisualAt(
        { x: saveCenter.x, y: saveCenter.y - DROP_OFFSET_Y },
        { opacity: 0.92, scale: 0.94 }
      ),
      save: saveVisual,
      history: pillVisualAt(historyCenter, { opacity: 0, blurPx: 8, scale: 0.82 })
    };
  }, []);

  const reset = useCallback(() => {
    setPhase('idle');
    setRuleText('');
    setIsSaving(false);
    setShowPropagate(false);
    setShowFly(false);
    setFlyVisual(INITIAL_FLY);
    setSaveHighlight(false);
    setHistoryRipple(false);
    setPointerClicking(false);
  }, []);

  const runSaveFly = useCallback(
    async (isCancelled: () => boolean) => {
      const targets = measureFlyTargets();
      if (!targets) return;

      setPhase('fly');
      setFlyVisual(targets.dropStart);
      setShowFly(true);
      await sleep(TIMING_MS.layoutSettle, isCancelled);
      if (isCancelled()) return;

      await tweenFly(
        { ...targets.dropStart, opacity: 1, scale: 1 },
        targets.save,
        TIMING_MS.flyDrop,
        EASE_DROP_ON_SAVE,
        setFlyVisual,
        isCancelled,
        { arcLift: 0.2 }
      );
      if (isCancelled()) return;

      await sleep(TIMING_MS.flyHoldOnSave, isCancelled);
      if (isCancelled()) return;

      let rippleStarted = false;
      await tweenFly(
        targets.save,
        targets.history,
        TIMING_MS.flyToHistory,
        EASE_DROP_TO_HISTORY,
        setFlyVisual,
        isCancelled,
        {
          arcLift: 0.55,
          onProgress: (t) => {
            if (!rippleStarted && t >= 0.58) {
              rippleStarted = true;
              setHistoryRipple(true);
            }
          }
        }
      );
      if (isCancelled()) return;

      setShowFly(false);
      setFlyVisual(INITIAL_FLY);
    },
    [measureFlyTargets]
  );

  const runLoop = useCallback(
    async (isCancelled: () => boolean) => {
      await sleep(TIMING_MS.holdStart, isCancelled);
      if (isCancelled()) return;

      while (!isCancelled()) {
        reset();
        await sleep(TIMING_MS.layoutSettle, isCancelled);
        if (isCancelled()) break;

        setPhase('typing');
        for (let i = 1; i <= TYPING_RULE_TEXT.length; i++) {
          if (isCancelled()) break;
          setRuleText(TYPING_RULE_TEXT.slice(0, i));
          await sleep(TIMING_MS.charInterval, isCancelled);
        }
        if (isCancelled()) break;

        setPhase('typedHold');
        await sleep(TIMING_MS.typedHold, isCancelled);
        if (isCancelled()) break;

        setPhase('saving');
        setSaveHighlight(true);
        await sleep(TIMING_MS.layoutSettle, isCancelled);
        if (isCancelled()) break;
        const stage = stageRef.current;
        const save = saveRef.current;
        if (stage && save) {
          const pt = centerOf(save, stage.getBoundingClientRect());
          setCursor(pt);
        }
        await mockPointerClick(setPointerClicking, isCancelled);
        if (isCancelled()) break;
        setIsSaving(true);
        await sleep(TIMING_MS.saving, isCancelled);
        if (isCancelled()) break;
        setIsSaving(false);
        setSaveHighlight(false);

        await runSaveFly(isCancelled);
        if (isCancelled()) break;

        setPhase('propagate');
        setShowPropagate(true);
        await sleep(TIMING_MS.propagate, isCancelled);
        if (isCancelled()) break;

        setPhase('hold');
        await sleep(TIMING_MS.holdEnd, isCancelled);
      }
    },
    [reset, runSaveFly]
  );

  useLayoutEffect(() => {
    if (!enabled) {
      setMeasureReady(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      setMeasureReady(measureFlyTargets() !== null);
    });
    return () => cancelAnimationFrame(id);
  }, [enabled, measureFlyTargets]);

  useEffect(() => {
    if (!enabled || !measureReady) return;

    cancelledRef.current = false;
    const isCancelled = () => cancelledRef.current;

    void runLoop(isCancelled);

    return () => {
      cancelledRef.current = true;
    };
  }, [enabled, measureReady, runLoop]);

  const showCursor =
    phase === 'typing' || phase === 'typedHold' || phase === 'saving';

  return {
    stageRef,
    saveRef,
    historyTabRef,
    phase,
    ruleText,
    activeTab,
    isSaving,
    showPropagate,
    showFly,
    flyVisual,
    saveHighlight,
    historyRipple,
    pointerClicking,
    showCursor,
    charCount: ruleText.length,
    pointerX: cursor.x,
    pointerY: cursor.y,
    showPointer: saveHighlight || pointerClicking
  };
}
